import { Fragment, useCallback, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import GlobalTabs from './GlobalTabs';
import type { ViewLayoutMode } from '../hooks/useViewLayoutMode';

export type ViewSection = {
  id: string;
  label: string;
  content: ReactNode;
};

type ViewSectionsProps = {
  mode: ViewLayoutMode;
  sections: ViewSection[];
  /** Maps legacy or alternate ?tab values to a section id (e.g. { updates: 'notes' }). */
  tabAliases?: Record<string, string>;
};

const TAB_PARAM = 'tab';

/**
 * Renders a view's sections as toggleable tabs (the default layout) or stacked.
 * Falls back to stacked rendering when there are fewer than two sections, since
 * a single tab adds no value.
 *
 * When tabbed, the active section is mirrored to the `?tab` query param (by
 * section id) so the open tab survives reloads and is shareable.
 */
export function ViewSections({ mode, sections, tabAliases }: ViewSectionsProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const showTabs = mode === 'tabs' && sections.length > 1;

  const activeTabId = searchParams.get(TAB_PARAM);
  const resolvedTabId = (activeTabId && tabAliases?.[activeTabId]) || activeTabId;
  const activeIndexFromParam = sections.findIndex((section) => section.id === resolvedTabId);
  const initialIndex = activeIndexFromParam >= 0 ? activeIndexFromParam : 0;

  const handleTabChange = useCallback(
    (index: number) => {
      const section = sections[index];
      if (!section) return;
      setSearchParams(
        (prev) => {
          const params = new URLSearchParams(prev);
          params.set(TAB_PARAM, section.id);
          return params;
        },
        { replace: true },
      );
    },
    [sections, setSearchParams],
  );

  if (showTabs) {
    return (
      <GlobalTabs
        initialIndex={initialIndex}
        onChange={handleTabChange}
        tabs={sections.map((section) => ({ label: section.label, content: section.content }))}
      />
    );
  }

  return (
    <>
      {sections.map((section) => (
        <Fragment key={section.id}>{section.content}</Fragment>
      ))}
    </>
  );
}
